import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOpsAuth } from '@/ops/OpsAuthContext';
import { Search, ChevronRight, X } from 'lucide-react';

export default function OpsUsersPage() {
  const { opsAxios, OPS_API } = useOpsAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async (q = '') => {
    setLoading(true);
    try {
      const res = await opsAxios({ method: 'GET', url: `${OPS_API}/users`, params: { search: q || undefined, limit: 100 } });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="ops-users-page">
      <div className="mb-6">
        <h1 className="ops-h1">Users</h1>
        <p className="ops-sub mt-1"><span className="ops-mono" style={{ color: 'var(--ops-text-2)' }}>{total}</span> accounts · metadata only, no content</p>
      </div>

      <form onSubmit={e => { e.preventDefault(); load(search); }} className="flex gap-2 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--ops-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="ops-users-search"
            placeholder="Search by name or email…" className="ops-input w-full h-10 pl-9 pr-9" style={{ fontSize: 16 }} />
          {search && (
            <button type="button" onClick={() => { setSearch(''); load(''); }} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ops-muted)' }}>
              <X size={13} />
            </button>
          )}
        </div>
        <button type="submit" className="ops-btn ops-btn-secondary h-10 px-4">Search</button>
      </form>

      <div className="ops-card overflow-hidden">
        <table className="ops-table">
          <thead><tr>{['Name / Email', 'Vertical', 'Region', 'Cards', 'Tasks', 'Joined', 'Status', ''].map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center ops-sub" style={{ padding: '40px' }}>Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={8} className="text-center ops-sub" style={{ padding: '40px' }}>No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="cursor-pointer"
                onClick={() => { window.location.href = `/ops/users/${u.id}`; }}>
                <td>
                  <Link to={`/ops/users/${u.id}`} onClick={e => e.stopPropagation()}>
                    <p className="font-medium" style={{ color: 'var(--ops-text)' }}>{u.name}</p>
                    <p className="ops-mono text-xs" style={{ color: 'var(--ops-muted)' }}>{u.email}</p>
                  </Link>
                </td>
                <td className="capitalize">{u.vertical || '—'}</td>
                <td className="ops-mono text-xs" data-testid={`user-region-${u.id}`} style={{ color: 'var(--ops-text-2)' }}>
                  {u.home_region || <span style={{ color: 'var(--ops-muted)' }}>unset</span>}
                </td>
                <td className="ops-mono" style={{ color: 'var(--ops-text-2)' }}>{u.usage?.action_cards ?? 0}</td>
                <td className="ops-mono" style={{ color: 'var(--ops-text-2)' }}>{u.usage?.tasks ?? 0}</td>
                <td className="ops-mono" style={{ color: 'var(--ops-muted)' }}>{new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</td>
                <td><span className={`ops-badge ${u.is_suspended ? 'ops-badge-danger' : 'ops-badge-success'}`}>{u.is_suspended ? 'Suspended' : 'Active'}</span></td>
                <td><ChevronRight size={14} style={{ color: 'var(--ops-muted)' }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
